import { useEffect, useRef } from "react";

import * as Sentry from "@sentry/browser";
import { fetchQuery, useRelayEnvironment } from "react-relay";

import type { GraphQLTaggedNode, OperationType } from "relay-runtime";

interface UsePollingOptions<TData> {
  /** The GraphQL query to poll */
  query: GraphQLTaggedNode;
  /** Variables for the query */
  variables: Record<string, unknown>;
  /** Interval in milliseconds for polling */
  interval: number;
  /** Whether polling is enabled */
  enabled?: boolean;
  /** Callback when data is received. Return true to stop polling */
  onData?: (data: TData) => boolean | undefined;
  /** Callback when an error occurs. Polling stops on error by default */
  onError?: (error: Error) => void;
}

/**
 * Custom hook for polling GraphQL queries at regular intervals
 * @param options - Polling configuration options
 *
 * @example
 * ```ts
 * usePolling({
 *   query: PollJiraInstanceQuery,
 *   variables: { uuid: jiraInstanceUuid },
 *   interval: 2000,
 *   enabled: Boolean(jiraInstanceUuid),
 *   onData: (data) => {
 *     if (data.jiraInstance?.spacesUpdated) {
 *       setSpaces(data.jiraInstance.spaces);
 *       return true; // Stop polling
 *     }
 *   },
 *   onError: (error) => {
 *     setFormErrors([error.message]);
 *   }
 * });
 * ```
 */
export function usePolling<TQuery extends OperationType>(
  options: UsePollingOptions<TQuery["response"]>,
): void {
  const {
    query,
    variables,
    interval,
    enabled = true,
    onData,
    onError,
  } = options;
  const environment = useRelayEnvironment();

  // Store callbacks in refs to avoid restarting polling when they change
  const onDataRef = useRef(onData);
  const onErrorRef = useRef(onError);

  // Update refs when callbacks change
  useEffect(() => {
    onDataRef.current = onData;
    onErrorRef.current = onError;
  }, [onData, onError]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let shouldStop = false;
    let pollInterval: NodeJS.Timeout | null = null;
    const subscriptions: { unsubscribe: () => void }[] = [];

    const executeFetch = () => {
      if (shouldStop) {
        return;
      }

      const subscription = fetchQuery<TQuery>(
        environment,
        query,
        variables,
      ).subscribe({
        next: (data) => {
          if (shouldStop) {
            return;
          }
          const stopPolling = onDataRef.current?.(data);
          if (stopPolling) {
            shouldStop = true;
            if (pollInterval) {
              clearInterval(pollInterval);
            }
          }
        },
        error: (error: Error) => {
          if (shouldStop) {
            return;
          }
          Sentry.captureException(error);
          onErrorRef.current?.(error);
          shouldStop = true;
          if (pollInterval) {
            clearInterval(pollInterval);
          }
        },
      });
      subscriptions.push(subscription);
    };

    // Execute first fetch immediately
    executeFetch();

    // Then start interval for subsequent polls
    pollInterval = setInterval(executeFetch, interval);

    return () => {
      shouldStop = true;
      if (pollInterval) {
        clearInterval(pollInterval);
      }
      subscriptions.forEach((sub) => sub.unsubscribe());
    };
  }, [query, variables, interval, enabled, environment]);
}
